import { Stack, Tab } from '@mui/material';
import React, { useMemo, ReactNode } from 'react';
import { Link, Navigate, NavLink, Routes, useLocation } from 'react-router-dom';

import { AddIcon, Button, StatusChip } from '@occc/ui-design-system';
import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';
import { Tabs } from '@occc/ui-design-system';
import { SentryRoute as Route } from '@occc-app/App/sentry';
import { LoadedData } from '@occc/utils';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';
import { ScanDataWeekEndingOverviewFragment } from '@occc/graphql';
import { formatDateTime, DateTimeFormat } from '@occc-app/lib/date';

import { breadcrumbs } from './breadcrumbs';

import { WeekSelectorButton } from '../../components/week-selector/week-selector-button.component';
import { getMostRecentWednesday } from '../../components/week-selector/week-selector.utils';
import { ScanDataWeeklyFileContainer } from '../scan-data-weekly-file/scan-data-weekly-file.container';
import { ScanDataSubmissionGroups } from '../scan-data-submission-groups';
import { TabValue } from '../../scan-data.types';
import { routeCreator } from '../../scan-data.router';
import { useScanDataWeekEndingOverview } from '../../hooks/use-scan-data-week-ending-overview.hook';
import { getScanDataWeekEndingDate } from '../../scan-data.utils';
import { scanDataFileStatusToLabelMap } from '../scan-data-weekly-file/scan-data-weekly-file.constants';
import {
  OVERVIEW_TAB_CONFIG,
  SUBMISSION_GROUPS_TAB_CONFIG,
  WEEKLY_FILE_TAB_CONFIG,
} from '../../scan-data.constants';
import { useGetScanDataSftpCredentialsOverview } from '../../hooks/use-get-scan-data-sftp-credentials-overview';
import { ScanDataOverviewController } from '../scan-data-overview/scan-data-overview.controller';

export const ScanDataPage = () => {
  const location = useLocation();
  const isCreateGroupAllowed = useIsUserPermittedTo(Feature.SCAN_DATA_SUBMISSION_GROUPS_CREATE);

  useAppLayoutContextConsumer(breadcrumbs);

  const [selectedWeek, setSelectedWeek] = React.useState<Date>(() => getMostRecentWednesday());

  const submissionDateEquals = useMemo(
    () => formatDateTime(selectedWeek, DateTimeFormat.DateDashed),
    [selectedWeek],
  );

  const {
    loading: overviewLoading,
    error: overviewError,
    data: overviewData,
  } = useGetScanDataSftpCredentialsOverview();

  let submissionGroupsTotal = undefined;

  if (overviewData) {
    const { totalAltria, totalMSA, totalRJR } = overviewData.scanDataSftpCredentialsOverview;
    submissionGroupsTotal = totalAltria + totalMSA + totalRJR;
  }

  const weekEndingDate = getScanDataWeekEndingDate();
  const scanDataWeekEndingOverviewResponse = useScanDataWeekEndingOverview(weekEndingDate);

  const weekEndingOverviewLoadedData: LoadedData<ScanDataWeekEndingOverviewFragment> =
    useMemo(() => {
      return {
        data: scanDataWeekEndingOverviewResponse.data?.scanDataWeekEndingOverview,
        loading: scanDataWeekEndingOverviewResponse.loading,
        error: scanDataWeekEndingOverviewResponse.error,
      };
    }, [
      scanDataWeekEndingOverviewResponse.data?.scanDataWeekEndingOverview,
      scanDataWeekEndingOverviewResponse.error,
      scanDataWeekEndingOverviewResponse.loading,
    ]);

  const fileStatus = scanDataWeekEndingOverviewResponse.data?.scanDataWeekEndingOverview.fileStatus;

  const tabConfigItems: {
    label: ReactNode;
    value: TabValue;
    pageTitle: string;
    pageSubtitle: string;
  }[] = useMemo(() => {
    return [
      OVERVIEW_TAB_CONFIG,
      {
        ...WEEKLY_FILE_TAB_CONFIG,
        label: (
          <Stack direction='row' alignItems='center' gap={1}>
            Weekly File
            {fileStatus && <StatusChip label={scanDataFileStatusToLabelMap[fileStatus]} />}
          </Stack>
        ),
      },
      {
        ...SUBMISSION_GROUPS_TAB_CONFIG,
        label: (
          <Stack direction='row' alignItems='center' gap={1}>
            Submission Groups
            {submissionGroupsTotal !== null && submissionGroupsTotal !== undefined && (
              <StatusChip label={submissionGroupsTotal.toString()} />
            )}
          </Stack>
        ),
      },
    ];
  }, [fileStatus, submissionGroupsTotal]);

  const currentTabConfig =
    tabConfigItems.find((tab) => location.pathname.includes(tab.value)) ?? tabConfigItems[0];

  const renderPageActions = () => {
    if (currentTabConfig.value === 'submission-groups' && isCreateGroupAllowed) {
      return (
        <Button
          color='secondary'
          variant='contained'
          component={Link}
          to={routeCreator.submissionGroupsCreate()}
          startIcon={<AddIcon />}
          role='button'
        >
          Create Group
        </Button>
      );
    }

    if (currentTabConfig.value === 'overview') {
      return <WeekSelectorButton value={selectedWeek} onChange={setSelectedWeek} />;
    }

    return null;
  };

  return (
    <Page
      browserTabTitle={currentTabConfig.pageTitle}
      pageHeaderProps={{
        title: currentTabConfig.pageTitle,
        subtitle: currentTabConfig.pageSubtitle,
      }}
      containerProps={{ variant: 'md' }}
      actions={renderPageActions()}
    >
      <Tabs value={currentTabConfig.value} color='secondary'>
        {tabConfigItems.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            component={NavLink}
            to={tab.value}
          />
        ))}
      </Tabs>

      <Routes>
        <Route
          path={`/${'overview' satisfies TabValue}`}
          element={<ScanDataOverviewController submissionDateEquals={submissionDateEquals} />}
        />
        <Route
          path={`/${'weekly-file' satisfies TabValue}`}
          element={
            <ScanDataWeeklyFileContainer
              weekEndingDate={weekEndingDate}
              weekEndingOverviewLoadedData={weekEndingOverviewLoadedData}
            />
          }
        />
        <Route
          path={`/${'submission-groups' satisfies TabValue}`}
          element={
            <ScanDataSubmissionGroups
              overviewLoading={overviewLoading}
              overviewError={overviewError}
              overviewData={overviewData?.scanDataSftpCredentialsOverview}
            />
          }
        />
        <Route path='*' element={<Navigate to='overview' replace />} />
      </Routes>
    </Page>
  );
};
